import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { I18nService } from '../../services/i18n.service';
import { ProductsService } from '../../services/products.service';

@Component({
  selector: 'ds-footer',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (f(); as f) {
    <footer class="ds-footer">
      <div class="ds-footer__inner">

        <!-- Newsletter (désactivé temporairement — décommenter pour réactiver)
        <div class="ds-footer__newsletter">
          <div class="ds-footer__nl-text">
            <div class="ds-footer__eyebrow">{{ f.newsletter.eyebrow }}</div>
            <h3 class="ds-footer__nl-title">{{ f.newsletter.title }}</h3>
            <p class="ds-footer__nl-desc">{{ f.newsletter.desc }}</p>
          </div>
          <div class="ds-footer__nl-form">
            <input class="ds-footer__nl-input" type="email" [placeholder]="f.newsletter.placeholder" />
            <button class="ds-footer__nl-btn" type="button">{{ f.newsletter.cta }}</button>
            <span class="ds-footer__nl-caption">{{ f.newsletter.caption }}</span>
          </div>
        </div>
        -->

        <!-- Brand + nav columns -->
        <div class="ds-footer__cols">

          <!-- Brand -->
          <div class="ds-footer__brand">
            <div class="ds-footer__logo">
              <span class="ds-footer__logo-mark" aria-hidden="true">D</span>
              <span class="ds-footer__logo-name">DemandSens</span>
            </div>
            <p class="ds-footer__tagline">{{ f.tagline }}</p>
          </div>

          <!-- Nav columns -->
          @for (col of f.cols; track col.title) {
            <div>
              <div class="ds-footer__col-title">{{ col.title }}</div>
              @for (link of col.links; track link; let i = $index) {
                @let href = col.hrefs?.[i] ?? null;
                @if (href) {
                  @let resolved = resolveHref(href);
                  @let external = resolved.startsWith('http');
                  <a
                    [href]="resolved"
                    class="ds-footer__link"
                    [attr.target]="external ? '_blank' : null"
                    [attr.rel]="external ? 'noopener noreferrer' : null"
                  >{{ link }}</a>
                } @else {
                  <a href="#" class="ds-footer__link">{{ link }}</a>
                }
              }
            </div>
          }

        </div>

        <!-- Bottom bar -->
        <div class="ds-footer__bottom">
          <span>{{ f.bottom_copy }}</span>
        </div>

      </div>
    </footer>
    }
  `,
  styles: [`
    .ds-footer {
      background: var(--surface-secondary);
      border-top: 1px solid var(--border);
      color: var(--on-surface);
      font-family: var(--f-display);
      padding: 80px 64px 40px;

      @media (max-width: 768px) { padding: 56px 24px 32px; }
    }

    .ds-footer__inner {
      max-width: 1280px;
      margin: 0 auto;
    }

    /* ── Newsletter ───────────────────────────────────────────── */
    .ds-footer__newsletter {
      display: grid;
      grid-template-columns: 1.4fr 1fr;
      gap: 64px;
      padding-bottom: 56px;
      border-bottom: 1px solid var(--border);
      margin-bottom: 56px;

      @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 32px;
      }
    }

    .ds-footer__eyebrow {
      font-family: var(--f-mono);
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: #CC2831;
      margin-bottom: 14px;

      :where(.dark) & { color: #FF6B6B; }
    }

    .ds-footer__nl-title {
      font-family: var(--f-display);
      font-size: 32px;
      font-weight: 700;
      letter-spacing: -0.02em;
      margin: 0 0 8px;
      max-width: 540px;
      line-height: 1.15;
      text-wrap: balance;
    }

    .ds-footer__nl-desc {
      font-family: var(--f-body);
      font-size: 15px;
      line-height: 1.55;
      color: var(--on-surface-muted);
      margin: 0;
      max-width: 480px;
    }

    .ds-footer__nl-form {
      display: flex;
      flex-direction: column;
      gap: 12px;
      justify-content: center;
    }

    .ds-footer__nl-input {
      width: 100%;
      border: 1px solid var(--border);
      border-radius: var(--r-sm);
      padding: 10px 14px;
      font-family: var(--f-body);
      font-size: 14px;
      background: var(--surface);
      color: var(--on-surface);
      outline: none;
      transition: border-color var(--d-2) var(--e-standard), box-shadow var(--d-2) var(--e-standard);
      box-sizing: border-box;

      &::placeholder { color: var(--on-surface-muted); }

      &:focus {
        border-color: var(--c-focus);
        box-shadow: 0 0 0 3px rgba(166, 135, 255, 0.25);
      }
    }

    .ds-footer__nl-btn {
      background: var(--c-accent);
      color: var(--c-accent-ink);
      border: none;
      border-radius: var(--r-sm);
      padding: 12px 20px;
      font-family: var(--f-body);
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      width: 100%;
      transition: background-color var(--d-2) var(--e-standard), transform var(--d-2) var(--e-decel);

      &:hover { background-color: var(--c-accent-hover); transform: translateY(-1px); }
      &:active { transform: translateY(0); transition-duration: 80ms; }
      &:focus-visible { outline: 2px solid var(--c-focus); outline-offset: 2px; }
    }

    .ds-footer__nl-caption {
      font-family: var(--f-body);
      font-size: 12px;
      color: var(--on-surface-muted);
    }

    /* ── Columns ──────────────────────────────────────────────── */
    .ds-footer__cols {
      display: grid;
      grid-template-columns: 1.6fr 1fr 1fr 1fr;
      gap: 48px;
      margin-bottom: 56px;

      @media (max-width: 1024px) { grid-template-columns: 1fr 1fr 1fr; }
      @media (max-width: 640px)  { grid-template-columns: 1fr 1fr; }
    }

    /* ── Brand ────────────────────────────────────────────────── */
    .ds-footer__brand {
      display: flex;
      flex-direction: column;
    }

    .ds-footer__logo {
      display: inline-flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 18px;
    }

    .ds-footer__logo-mark {
      width: 28px;
      height: 28px;
      border-radius: 6px;
      background: var(--c-accent);
      color: var(--c-accent-ink);
      font-size: 14px;
      font-weight: 700;
      display: grid;
      place-items: center;
      flex-shrink: 0;
    }

    .ds-footer__logo-name {
      font-size: 17px;
      font-weight: 700;
      letter-spacing: -0.01em;
    }

    .ds-footer__tagline {
      font-family: var(--f-body);
      font-size: 14px;
      line-height: 1.55;
      color: var(--on-surface-muted);
      margin: 0 0 18px;
      max-width: 320px;
    }

    /* ── Nav column ───────────────────────────────────────────── */
    .ds-footer__col-title {
      font-family: var(--f-mono);
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: var(--on-surface-muted);
      margin-bottom: 16px;
    }

    .ds-footer__link {
      display: block;
      font-size: 14px;
      font-weight: 700;
      text-decoration: none;
      color: var(--on-surface);
      margin-bottom: 10px;
      transition: opacity var(--d-2) var(--e-standard);

      &:hover { opacity: 0.7; }
      &:focus-visible { outline: 2px solid var(--c-focus); outline-offset: 2px; border-radius: 2px; }
    }

    /* ── Bottom bar ───────────────────────────────────────────── */
    .ds-footer__bottom {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-top: 24px;
      border-top: 1px solid var(--border);
      font-family: var(--f-mono);
      font-size: 12px;
      color: var(--on-surface-muted);
      letter-spacing: 0.04em;
      gap: 16px;
      flex-wrap: wrap;
    }
  `],
})
export class DsFooterComponent {
  private readonly i18n = inject(I18nService);
  private readonly products = inject(ProductsService);
  readonly f = computed(() => this.i18n.translations()?.footer ?? null);

  resolveHref(href: string): string {
    return href.replace(/\{\{(\w+)\.email\}\}/g, (_, key) =>
      this.products.getProduct(key)?.email ?? '',
    );
  }
}
